// Pooled floating combat / pickup text. Numbers rise from where things happen,
// pop in scale, then drift up and fade. Crits and milestones float bigger and
// brighter. Text objects are reused from a pool to avoid per-hit allocation.

import { Container, Text } from 'pixi.js';

interface Floater {
  text: Text;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  popTime: number;
  baseSize: number;
  active: boolean;
}

export interface FloatOptions {
  color?: number;
  size?: number; // font size in px
  life?: number; // seconds
  vx?: number;
  vy?: number; // upward is negative
}

export class FloaterSystem {
  private pool: Floater[] = [];

  constructor(private readonly layer: Container) {}

  private obtain(): Floater {
    for (const f of this.pool) {
      if (!f.active) return f;
    }
    const text = new Text({
      text: '',
      style: {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 16,
        fontWeight: '800',
        fill: 0xffffff,
        stroke: { color: 0x10131a, width: 4 },
        align: 'center',
      },
    });
    text.anchor.set(0.5);
    text.visible = false;
    this.layer.addChild(text);
    const f: Floater = {
      text,
      vx: 0,
      vy: 0,
      life: 0,
      maxLife: 1,
      popTime: 0,
      baseSize: 16,
      active: false,
    };
    this.pool.push(f);
    return f;
  }

  spawn(x: number, y: number, str: string, opts: FloatOptions = {}): void {
    const f = this.obtain();
    const size = opts.size ?? 16;
    f.text.text = str;
    f.text.style.fontSize = size;
    f.text.style.fill = opts.color ?? 0xffffff;
    f.baseSize = size;
    f.text.x = x;
    f.text.y = y;
    f.vx = opts.vx ?? (Math.random() - 0.5) * 24;
    f.vy = opts.vy ?? -46;
    f.life = opts.life ?? 0.9;
    f.maxLife = f.life;
    f.popTime = Math.min(0.12, f.life * 0.3);
    f.active = true;
    f.text.visible = true;
    f.text.alpha = 1;
    f.text.scale.set(0.2);
  }

  update(dt: number): void {
    for (const f of this.pool) {
      if (!f.active) continue;
      f.life -= dt;
      if (f.life <= 0) {
        f.active = false;
        f.text.visible = false;
        continue;
      }
      f.text.x += f.vx * dt;
      f.text.y += f.vy * dt;
      f.vy += 36 * dt; // gentle deceleration of the upward drift

      const elapsed = f.maxLife - f.life;
      // pop-in scale, then settle to 1, then nothing (fade handles the exit)
      let scale = 1;
      if (elapsed < f.popTime) {
        const t = elapsed / f.popTime;
        scale = 0.2 + 1.1 * t; // overshoot a touch
      } else {
        const settle = Math.min(1, (elapsed - f.popTime) / 0.1);
        scale = 1.3 - 0.3 * settle;
      }
      f.text.scale.set(scale);

      const fadeT = f.life / f.maxLife;
      f.text.alpha = Math.min(1, fadeT * 1.6);
    }
  }
}
